// Per-line cart note ("no onions", "extra spicy"). Shows an "Add note" link,
// the saved note with an edit action, or an inline editor. The parent owns the
// save (it PATCHes the cart line); `onSave` resolves truthy on success.
import { useEffect, useRef, useState } from 'react';
import { Input } from 'antd';
import { EditOutlined, FormOutlined } from '@ant-design/icons';

const CART_LINE_NOTE_MAX = 200;

export default function CartLineNote({ value = '', onSave, isSaving = false, t }) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isEditing) setDraft(value);
  }, [value, isEditing]);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus({ cursor: 'end' });
  }, [isEditing]);

  const save = async () => {
    const next = draft.trim();
    if (next === value.trim()) {
      setIsEditing(false);
      return;
    }
    if (await onSave?.(next)) setIsEditing(false);
  };

  const cancel = () => {
    setDraft(value);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="cart-line-note is-editing">
        {/* Composer: borderless textarea + footer bar inside one framed box. */}
        <div className="cart-line-note__editor">
          <Input.TextArea
            ref={inputRef}
            variant="borderless"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') cancel();
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) save();
            }}
            maxLength={CART_LINE_NOTE_MAX}
            autoSize={{ minRows: 2, maxRows: 4 }}
            placeholder={t('itemNotePlaceholder', 'E.g. less spicy, no onions')}
            aria-label={t('itemNote', 'Note for this item')}
            disabled={isSaving}
          />
          <div className="cart-line-note__actions">
            <span className="cart-line-note__count" aria-live="polite">
              {draft.length}/{CART_LINE_NOTE_MAX}
            </span>
            <button
              type="button"
              className="cart-line-note__btn"
              onClick={cancel}
              disabled={isSaving}
            >
              {t('cancel', 'Cancel')}
            </button>
            <button
              type="button"
              className="cart-line-note__btn is-primary"
              onClick={save}
              disabled={isSaving}
              aria-busy={isSaving}
            >
              {isSaving ? t('saving', 'Saving…') : t('save', 'Save')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (value) {
    return (
      <div className="cart-line-note">
        <p className="cart-line-note__text">
          <FormOutlined aria-hidden /> {value}
        </p>
        <button
          type="button"
          className="cart-line-note__link"
          onClick={() => setIsEditing(true)}
          aria-label={t('editNote', 'Edit note')}
        >
          <EditOutlined aria-hidden /> {t('edit', 'Edit')}
        </button>
      </div>
    );
  }

  return (
    <div className="cart-line-note">
      <button type="button" className="cart-line-note__link" onClick={() => setIsEditing(true)}>
        <FormOutlined aria-hidden /> {t('addNote', 'Add note')}
      </button>
    </div>
  );
}
